// AI-assisted: Claude 2026-08-07, human-reviewed: ausstehend
// Fabelwesen-Overlay: hört auf "fithub:celebrate" (siehe lib/celebrate.ts).
// Klein (save): nur ein kleines Wesen. Groß (day-complete/milestone/workout-complete):
// großes Wesen + Konfetti + Text-Pille. Bei prefers-reduced-motion: gar nichts —
// der normale Toast übernimmt die Text-Rückmeldung.
import { useEffect, useRef, useState } from "react"
import type { ComponentType } from "react"
import type { CelebrateEvent, FeierStufe } from "@/lib/celebrate"
import { feierStufe } from "@/lib/celebrate"
import Fuchs from "./creatures/Fuchs"
import Gluehwuermchen from "./creatures/Gluehwuermchen"
import Sproessling from "./creatures/Sproessling"
import Igel from "./creatures/Igel"
import Eule from "./creatures/Eule"
import Wichtel from "./creatures/Wichtel"
import Confetti from "./Confetti"
import "@/styles/celebration.css"

const WESEN: ComponentType[] = [Fuchs, Gluehwuermchen, Sproessling, Igel, Eule, Wichtel]

// Merkt sich das zuletzt gezeigte Wesen und wählt eines der anderen fünf.
let letztes = -1
function pickWesen(): ComponentType {
  let i = Math.floor(Math.random() * (letztes < 0 ? WESEN.length : WESEN.length - 1))
  if (letztes >= 0 && i >= letztes) i++
  letztes = i
  return WESEN[i]
}

// Muss zu den Animationsdauern in styles/celebration.css passen.
const KLEIN_MS = 1600
const GROSS_MS = 2600
const PRAESENT_MS = 3400

/** Feier-Stufe (Optionen): wie groß, wie viel Konfetti, wie lange. */
const STUFEN = {
  dezent: { klasse: "cel-dezent", konfettiKlein: 0, konfettiGross: 0.5, extraMs: 0 },
  normal: { klasse: "cel-normal", konfettiKlein: 0, konfettiGross: 1, extraMs: 0 },
  praesent: { klasse: "cel-praesent", konfettiKlein: 0.8, konfettiGross: 2, extraMs: PRAESENT_MS - GROSS_MS },
} as const

type Anzeige = { gross: boolean; text: string | null; Wesen: ComponentType; nr: number; stufe: FeierStufe }

export default function Celebration() {
  const [anzeige, setAnzeige] = useState<Anzeige | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    function onCelebrate(e: Event) {
      if (
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      )
        return
      const ev = (e as CustomEvent<CelebrateEvent>).detail
      const gross = ev.kind !== "save"
      const text =
        ev.kind === "day-complete"
          ? `Alle Übungen von Tag ${ev.dayCode} geschafft – stark und stabil!`
          : ev.kind === "milestone" || ev.kind === "record"
            ? ev.text
            : ev.kind === "workout-complete"
              ? ev.summary
              : null
      // nr als key: bei schnell aufeinanderfolgenden Events starten die Animationen neu
      const stufe = feierStufe()
      setAnzeige((prev) => ({ gross, text, Wesen: pickWesen(), nr: (prev?.nr ?? 0) + 1, stufe }))
      clearTimeout(timer.current)
      const dauer = (gross ? GROSS_MS : KLEIN_MS) + (gross ? STUFEN[stufe].extraMs : 0)
      timer.current = setTimeout(() => setAnzeige(null), dauer)
    }
    window.addEventListener("fithub:celebrate", onCelebrate)
    return () => {
      window.removeEventListener("fithub:celebrate", onCelebrate)
      clearTimeout(timer.current)
    }
  }, [])

  if (!anzeige) return null
  const { gross, text, Wesen, nr, stufe } = anzeige
  const s = STUFEN[stufe]
  const konfetti = gross ? s.konfettiGross : s.konfettiKlein
  return (
    <div
      key={nr}
      className={`celebration ${gross ? "celebration--big" : "celebration--small"} ${s.klasse}`}
      data-testid="celebration"
      aria-hidden="true"
    >
      {konfetti > 0 && <Confetti menge={konfetti} />}
      <Wesen />
      {gross && text && <div className="celebration-text">{text}</div>}
    </div>
  )
}
